import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";

import { useTheme } from "../../contexts/ThemeContext";
import { useAuth } from "../../contexts/AuthContext";
import LayoutDesktop from "../../layouts/LayoutDesktop";
import { db } from "../../config/firebase";
import { useGoogleMapsDistance } from "../../hooks/useGoogleMapsDistance";
import { apiMapsTolls } from "../../services/mapsConfigApi";

const COLLECTION_PROPOSTAS_PREVENTIVAS = "financeiro_propostas_preventivas";
const COLLECTION_VALORES_SERVICOS = "financeiro_valores_servicos";
const COLLECTION_SESSOES = "atendimentos_sessoes";
const COLLECTION_ATENDIMENTOS = "atendimentos_preventiva";
const COLLECTION_ITENS = "atendimentos_sessao_itens";
const TIPOS_EQUIPAMENTO = [
  "Pin Load",
  "Plate Load",
  "Banco",
  "Cross",
  "Guiado",
  "Livre",
  "Esteira",
  "Bike",
  "Spinning",
  "Eliptico",
  "Escada",
  "Remo",
];

function parseNumero(value) {
  let raw = String(value || "").trim();
  if (!raw) return 0;
  if (raw.includes(".") && raw.includes(",")) {
    raw = raw.replace(".", "").replace(",", ".");
  } else if (raw.includes(",")) {
    raw = raw.replace(",", ".");
  }
  const num = Number.parseFloat(raw);
  return Number.isNaN(num) ? 0 : num;
}

function formatNumero(value, decimals = 2) {
  const num = Number(value);
  if (Number.isNaN(num)) return "";
  return num.toFixed(decimals);
}

function buildDefaultValoresPorTipo() {
  const init = {};
  TIPOS_EQUIPAMENTO.forEach((tipo) => {
    init[tipo] = 0;
  });
  return init;
}

function montarEndereco(data) {
  if (!data) return "";
  const parts = [
    data.enderecoCompleto,
    data.endereco,
    [data.logradouro, data.numero].filter(Boolean).join(", "),
    data.bairro,
    data.cidade,
    data.estado,
    data.cep,
  ]
    .map((v) => String(v || "").trim())
    .filter(Boolean);

  return Array.from(new Set(parts)).join(" - ");
}

function isContratoAvulso(value) {
  return String(value || "").toUpperCase().includes("AVULSO");
}

function normalizarStatusVisita(sessao) {
  const raw = String(sessao?.status || "").trim().toUpperCase();
  if (raw === "FINALIZADA") return "FINALIZADA";
  if (raw === "CANCELADA") return "CANCELADA";
  if (raw === "ATENDIDA" || raw === "REALIZADA") return "ATENDIDA";
  if (raw === "AGENDADA") return "AGENDADA";
  return "PLANEJADA";
}

export default function FinanceiroPreventivaFormPage() {
  const { tema } = useTheme();
  const { usuario } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();

  const [clientes, setClientes] = useState([]);
  const [academias, setAcademias] = useState([]);
  const [maquinas, setMaquinas] = useState([]);
  const [visitas, setVisitas] = useState([]);

  const [visitaId, setVisitaId] = useState("");
  const [atendimentoId, setAtendimentoId] = useState("");
  const [clienteId, setClienteId] = useState("");
  const [academiaId, setAcademiaId] = useState("");

  const [observacoes, setObservacoes] = useState("");
  const [enderecoDestino, setEnderecoDestino] = useState("");

  const [kmRodados, setKmRodados] = useState("");
  const [kmIda, setKmIda] = useState(0);
  const [kmVolta, setKmVolta] = useState(0);
  const [valorDeslocamento, setValorDeslocamento] = useState("");
  const [valorPedagio, setValorPedagio] = useState("");
  const [pedagioIda, setPedagioIda] = useState(0);
  const [pedagioVolta, setPedagioVolta] = useState(0);
  const [valorDesconto, setValorDesconto] = useState("");

  const [tipoViagem, setTipoViagem] = useState("CARRO");
  const [quantidadeAjudantes, setQuantidadeAjudantes] = useState("");
  const [gastosAlmoco, setGastosAlmoco] = useState("");
  const [previsaoDias, setPrevisaoDias] = useState("");

  const [enderecoOrigem, setEnderecoOrigem] = useState("");
  const [valorKmDeslocamento, setValorKmDeslocamento] = useState(1.2);
  const [idaVolta, setIdaVolta] = useState(true);

  const [quantEquipPorTipo, setQuantEquipPorTipo] = useState(() => {
    const init = {};
    TIPOS_EQUIPAMENTO.forEach((tipo) => {
      init[tipo] = 0;
    });
    return init;
  });
  const [valoresPreventivaPorTipo, setValoresPreventivaPorTipo] = useState(() => buildDefaultValoresPorTipo());

  const [erro, setErro] = useState("");
  const [sucesso, setSucesso] = useState("");
  const [carregando, setCarregando] = useState(false);
  const [carregandoRegistro, setCarregandoRegistro] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [calculandoDistancia, setCalculandoDistancia] = useState(false);

  const isEdicao = !!id;
  const grupoId = usuario?.grupoId || "";

  const { calcularDistancia, gerarUrlGoogleMaps } = useGoogleMapsDistance();

  const clienteSelecionado = useMemo(
    () => clientes.find((c) => String(c.id) === String(clienteId)) || null,
    [clientes, clienteId]
  );

  const academiaSelecionada = useMemo(
    () => academias.find((a) => String(a.id) === String(academiaId)) || null,
    [academias, academiaId]
  );

  const visitaSelecionada = useMemo(
    () => visitas.find((v) => String(v.id) === String(visitaId)) || null,
    [visitas, visitaId]
  );

  const maquinasFiltradas = useMemo(() => {
    const base = maquinas.filter((m) => String(m.empresaId || "") === String(clienteId || ""));
    if (!academiaId) return base;
    return base.filter((m) => String(m.setorId || "") === String(academiaId));
  }, [maquinas, clienteId, academiaId]);

  const totalEquipamentos = useMemo(
    () => Object.values(quantEquipPorTipo).reduce((acc, qtd) => acc + Number(qtd || 0), 0),
    [quantEquipPorTipo]
  );

  const valorEquipamentos = useMemo(
    () => TIPOS_EQUIPAMENTO.reduce(
      (acc, tipo) => acc + (Number(quantEquipPorTipo[tipo] || 0) * Number(valoresPreventivaPorTipo[tipo] || 0)),
      0
    ),
    [quantEquipPorTipo, valoresPreventivaPorTipo]
  );

  const valorTotal = useMemo(() => {
    const desloc = parseNumero(valorDeslocamento);
    const pedagio = parseNumero(valorPedagio);
    const almoco = parseNumero(gastosAlmoco);
    const desconto = parseNumero(valorDesconto);
    return desloc + pedagio + almoco + valorEquipamentos - desconto;
  }, [valorDeslocamento, valorPedagio, gastosAlmoco, valorDesconto, valorEquipamentos]);

  useEffect(() => {
    const carregarValores = async () => {
      if (!usuario?.grupoId) return;
      try {
        const snap = await getDoc(doc(db, COLLECTION_VALORES_SERVICOS, usuario.grupoId));
        if (!snap.exists()) return;
        const data = snap.data() || {};
        if (data.preventivaPorTipo && typeof data.preventivaPorTipo === "object") {
          setValoresPreventivaPorTipo((prev) => ({ ...prev, ...data.preventivaPorTipo }));
        }
      } catch (e) {
        console.warn("Erro ao carregar valores de preventiva por tipo:", e);
      }
    };

    carregarValores();
  }, [usuario?.grupoId]);

  useEffect(() => {
    const carregarBase = async () => {
      if (!usuario?.grupoId) return;
      setCarregando(true);
      try {
        const [snapClientes, snapMaquinas] = await Promise.all([
          getDocs(query(collection(db, "empresas"), where("grupoId", "==", usuario.grupoId))),
          getDocs(query(collection(db, "maquinas"), where("grupoId", "==", usuario.grupoId))),
        ]);

        setClientes(
          snapClientes.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .sort((a, b) => String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR"))
        );
        setMaquinas(snapMaquinas.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (e) {
        console.error("Erro ao carregar base de preventivas:", e);
        setErro("Erro ao carregar clientes/equipamentos.");
      } finally {
        setCarregando(false);
      }
    };

    carregarBase();
  }, [usuario?.grupoId]);

  useEffect(() => {
    const carregarEnderecoGrupo = async () => {
      if (!usuario?.grupoId) return;
      try {
        const grupoRef = doc(db, "grupos", usuario.grupoId);
        const grupoSnap = await getDoc(grupoRef);
        if (!grupoSnap.exists()) return;

        const grupoData = grupoSnap.data() || {};
        setEnderecoOrigem(grupoData.enderecoOrigem || "");
        if (grupoData.valorKmDeslocamento != null) {
          const parsed = Number.parseFloat(String(grupoData.valorKmDeslocamento));
          if (!Number.isNaN(parsed)) setValorKmDeslocamento(parsed);
        }
      } catch (e) {
        console.error("Erro ao carregar endereco do grupo:", e);
      }
    };

    carregarEnderecoGrupo();
  }, [usuario?.grupoId]);

  useEffect(() => {
    const carregarVisitas = async () => {
      if (!usuario?.grupoId) return;

      try {
        const snapSessoes = await getDocs(
          query(
            collection(db, COLLECTION_SESSOES),
            where("grupoId", "==", usuario.grupoId)
          )
        );

        const sessoes = snapSessoes.docs.map((d) => ({ id: d.id, ...d.data() }));
        const atendimentoIds = Array.from(
          new Set(sessoes.map((s) => String(s.atendimentoId || "")).filter(Boolean))
        );

        const atendimentos = await Promise.all(
          atendimentoIds.map(async (atId) => {
            try {
              const snap = await getDoc(doc(db, COLLECTION_ATENDIMENTOS, atId));
              if (snap.exists()) return { id: snap.id, ...snap.data() };
            } catch {
              return null;
            }
            return null;
          })
        );

        const atendimentosById = new Map(atendimentos.filter(Boolean).map((a) => [String(a.id), a]));

        const itensPorSessao = new Map();
        await Promise.all(
          sessoes.map(async (s) => {
            try {
              const snapItens = await getDocs(
                query(collection(db, COLLECTION_ITENS), where("sessaoId", "==", s.id))
              );
              itensPorSessao.set(s.id, snapItens.docs.map((d) => ({ id: d.id, ...d.data() })));
            } catch {
              itensPorSessao.set(s.id, []);
            }
          })
        );

        const filtradas = sessoes
          .filter((s) => {
            const atendimento = atendimentosById.get(String(s.atendimentoId || ""));
            if (!atendimento) return false;

            if (s.propostaId && (!isEdicao || String(s.propostaId) !== String(id))) return false;

            const status = normalizarStatusVisita(s);
            if (status !== "PLANEJADA") return false;

            const itens = itensPorSessao.get(s.id) || [];
            return itens.some((item) => String(item?.tipo || "").toUpperCase() === "PREVENTIVA");
          })
          .map((s) => {
            const atendimento = atendimentosById.get(String(s.atendimentoId || "")) || {};
            return {
              ...s,
              atendimento,
              clienteId: atendimento.clienteId || s.clienteId || null,
              academiaId: atendimento.academiaId || atendimento.setorId || s.setorId || null,
            };
          });

        setVisitas(filtradas);
      } catch (e) {
        console.error("Erro ao carregar visitas preventivas:", e);
        setVisitas([]);
      }
    };

    carregarVisitas();
  }, [usuario?.grupoId]);

  useEffect(() => {
    const carregarAcademias = async () => {
      if (!usuario?.grupoId || !clienteId) {
        setAcademias([]);
        setAcademiaId("");
        return;
      }

      try {
        const snap = await getDocs(
          query(
            collection(db, "setores"),
            where("grupoId", "==", usuario.grupoId),
            where("empresaId", "==", clienteId)
          )
        );

        setAcademias(
          snap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .sort((a, b) => String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR"))
        );
      } catch (e) {
        console.error("Erro ao carregar academias:", e);
        setAcademias([]);
      }
    };

    carregarAcademias();
  }, [usuario?.grupoId, clienteId]);

  useEffect(() => {
    if (!isEdicao || !id) return;

    const carregarRegistro = async () => {
      setCarregandoRegistro(true);
      try {
        const snap = await getDoc(doc(db, COLLECTION_PROPOSTAS_PREVENTIVAS, id));
        if (!snap.exists()) {
          setErro("Proposta nao encontrada.");
          return;
        }
        const data = snap.data() || {};
        setVisitaId(data.sessaoId || "");
        setAtendimentoId(data.atendimentoId || "");
        setClienteId(data.clienteId || "");
        setAcademiaId(data.academiaId || "");
        setObservacoes(data.observacoes || "");
        setEnderecoDestino(data.enderecoDestino || "");

        setKmRodados(String(data.kmRodados ?? ""));
        setValorDeslocamento(String(data.valorDeslocamento ?? ""));
        setValorPedagio(String(data.valorPedagio ?? ""));
        setValorDesconto(String(data.valorDesconto ?? ""));

        setTipoViagem(data.tipoViagem || "CARRO");
        setQuantidadeAjudantes(String(data.quantidadeAjudantes ?? ""));
        setGastosAlmoco(String(data.gastosAlmoco ?? ""));
        setPrevisaoDias(String(data.previsaoDias ?? ""));

        if (data.equipamentosPorTipo && typeof data.equipamentosPorTipo === "object") {
          setQuantEquipPorTipo((prev) => ({ ...prev, ...data.equipamentosPorTipo }));
        }
      } catch (e) {
        console.error("Erro ao carregar proposta:", e);
        setErro("Erro ao carregar proposta.");
      } finally {
        setCarregandoRegistro(false);
      }
    };

    carregarRegistro();
  }, [id, isEdicao]);

  useEffect(() => {
    if (!visitaSelecionada) return;
    setAtendimentoId(visitaSelecionada.atendimentoId || "");
    setClienteId(visitaSelecionada.clienteId || "");
    setAcademiaId(visitaSelecionada.academiaId || "");
  }, [visitaSelecionada]);

  useEffect(() => {
    const totalKm = idaVolta ? Number(kmIda || 0) + Number(kmVolta || 0) : Number(kmIda || 0);
    if (totalKm > 0) setKmRodados(totalKm.toString());
  }, [idaVolta, kmIda, kmVolta]);

  useEffect(() => {
    const totalPedagio = idaVolta ? Number(pedagioIda || 0) + Number(pedagioVolta || 0) : Number(pedagioIda || 0);
    setValorPedagio(formatNumero(totalPedagio));
  }, [idaVolta, pedagioIda, pedagioVolta]);

  useEffect(() => {
    if (tipoViagem === "AVIAO") return;
    const km = Number(kmRodados || 0);
    setValorDeslocamento(formatNumero(km * valorKmDeslocamento));
  }, [kmRodados, tipoViagem, valorKmDeslocamento]);

  useEffect(() => {
    const enderecoAcademia = montarEndereco(academiaSelecionada);
    const enderecoCliente = montarEndereco(clienteSelecionado);
    if (!isEdicao) setEnderecoDestino(enderecoAcademia || enderecoCliente || "");
  }, [academiaSelecionada, clienteSelecionado, isEdicao]);

  const handleCalcularDistancia = async () => {
    if (!enderecoOrigem) {
      setErro("Endereco de origem nao configurado. Configure em Opcoes > Grupo.");
      return;
    }

    if (!enderecoDestino || enderecoDestino.trim().length < 10) {
      setErro("Informe o endereco de destino completo (com numero, cidade e estado).");
      return;
    }

    if (!enderecoOrigem || enderecoOrigem.trim().length < 10) {
      setErro("Endereco de origem incompleto. Configure endereco completo em Opcoes > Grupo.");
      return;
    }

    setCalculandoDistancia(true);
    setErro("");
    setSucesso("");

    try {
      const resultadoIda = await calcularDistancia(grupoId, enderecoOrigem, enderecoDestino);
      const novoKmIda = Number(resultadoIda.distanceKm || 0);
      let novoKmVolta = 0;
      let totalKm = novoKmIda;

      if (idaVolta) {
        const resultadoVolta = await calcularDistancia(grupoId, enderecoDestino, enderecoOrigem);
        novoKmVolta = Number(resultadoVolta.distanceKm || 0);
        totalKm += novoKmVolta;
      }

      setKmIda(novoKmIda);
      setKmVolta(novoKmVolta);
      setKmRodados(String(totalKm));
      setSucesso(
        idaVolta
          ? `Ida: ${novoKmIda} km | Volta: ${novoKmVolta} km | Total: ${totalKm} km`
          : `Distancia: ${novoKmIda} km`
      );

      try {
        const tollIda = await apiMapsTolls({ grupoId, origin: enderecoOrigem, destination: enderecoDestino });
        const valorIda = Number(tollIda?.tollValue || 0);
        setPedagioIda(valorIda);

        if (idaVolta) {
          const tollVolta = await apiMapsTolls({ grupoId, origin: enderecoDestino, destination: enderecoOrigem });
          setPedagioVolta(Number(tollVolta?.tollValue || 0));
        } else {
          setPedagioVolta(0);
        }
      } catch (tollErr) {
        console.warn("Falha ao calcular pedagio:", tollErr);
      }
    } catch (e) {
      console.error("Erro ao calcular distancia:", e);
      setErro(e.message || "Erro ao calcular distancia");
    } finally {
      setCalculandoDistancia(false);
    }
  };

  const handleSalvar = async (event) => {
    event.preventDefault();
    setErro("");
    setSucesso("");

    if (!usuario?.grupoId) {
      setErro("Usuario sem grupoId. Faca login novamente.");
      return;
    }

    if (!clienteId) {
      setErro("Selecione o cliente.");
      return;
    }

    if (!academiaId) {
      setErro("Selecione a academia.");
      return;
    }

    if (!previsaoDias || parseNumero(previsaoDias) <= 0) {
      setErro("Informe a previsao de dias.");
      return;
    }

    setSalvando(true);
    try {
      const payload = {
        grupoId: usuario.grupoId,
        sessaoId: visitaId || null,
        atendimentoId: atendimentoId || null,
        clienteId,
        clienteNome: clienteSelecionado?.nome || clienteSelecionado?.razaoSocial || "",
        academiaId,
        academiaNome: academiaSelecionada?.nome || academiaSelecionada?.codigo || "",
        enderecoDestino: enderecoDestino || null,

        observacoes: observacoes.trim() || null,

        equipamentosTotal: totalEquipamentos,
        equipamentosPorTipo: quantEquipPorTipo,

        tipoViagem,
        quantidadeAjudantes: parseNumero(quantidadeAjudantes) || 0,
        gastosAlmoco: parseNumero(gastosAlmoco) || 0,
        previsaoDias: parseNumero(previsaoDias) || 0,

        kmRodados: parseNumero(kmRodados) || 0,
        valorDeslocamento: parseNumero(valorDeslocamento) || 0,
        valorPedagio: parseNumero(valorPedagio) || 0,
        valorDesconto: parseNumero(valorDesconto) || 0,
        valorEquipamentos,
        valoresPreventivaPorTipo,
        valorTotal,
        ...(!isEdicao ? { status: "CRIADA" } : {}),

        criadoPor: usuario?.uid || usuario?.id || "",
        updatedAt: serverTimestamp(),
      };

      if (isEdicao) {
        await updateDoc(doc(db, COLLECTION_PROPOSTAS_PREVENTIVAS, id), payload);
        setSucesso("Proposta de preventiva atualizada com sucesso.");
      } else {
        await addDoc(collection(db, COLLECTION_PROPOSTAS_PREVENTIVAS), {
          ...payload,
          createdAt: serverTimestamp(),
        });
        setSucesso("Proposta de preventiva salva com sucesso.");
      }

      setTimeout(() => navigate("/financeiro/preventivas"), 350);
    } catch (e) {
      console.error("Erro ao salvar proposta de preventiva:", e);
      setErro("Erro ao salvar proposta de preventiva.");
    } finally {
      setSalvando(false);
    }
  };

  return (
    <LayoutDesktop
      tema={tema}
      titulo={isEdicao ? "Editar Preventiva" : "Nova Preventiva"}
      subtitulo="Financeiro / Propostas / Preventivas"
    >
      <section className="pc-card pc-form-card">
        <header className="pc-form-header">
          <h3>{isEdicao ? "Editar proposta de preventiva" : "Nova proposta de preventiva"}</h3>
          <p>Preencha cliente/academia, valide quantidade por tipo e finalize o orcamento da preventiva.</p>
        </header>

        {carregandoRegistro ? (
          <p style={{ padding: 10 }}>Carregando proposta...</p>
        ) : (
          <form onSubmit={handleSalvar} className="pc-form">
            {erro && (
              <div style={{ background: "#7f1d1d", color: "#fecaca", padding: 8, borderRadius: 8, marginBottom: 12, fontSize: 14 }}>
                {erro}
              </div>
            )}

            {sucesso && (
              <div style={{ background: "#064e3b", color: "#d1fae5", padding: 8, borderRadius: 8, marginBottom: 12, fontSize: 14 }}>
                {sucesso}
              </div>
            )}

            <div className="pc-form-grid">
              <div className="pc-form-group pc-form-group-full">
                <label>Visita (Preventiva Avulsa)</label>
                <select
                  value={visitaId}
                  onChange={(e) => setVisitaId(e.target.value)}
                  className="pc-input"
                  disabled={carregando}
                >
                  <option value="">Sem visita (preencher cliente e academia manualmente)</option>
                  {visitas.map((v) => {
                    const clienteNome = clientes.find((c) => String(c.id) === String(v.clienteId))?.nome || "Cliente";
                    const academiaNome = academias.find((a) => String(a.id) === String(v.academiaId))?.nome || "Academia";
                    return (
                      <option key={v.id} value={v.id}>
                        {clienteNome} • {academiaNome} • {v.id.slice(0, 6)}
                      </option>
                    );
                  })}
                </select>
              </div>

              <div className="pc-form-group">
                <label>Selecionar cliente</label>
                <select value={clienteId} onChange={(e) => setClienteId(e.target.value)} className="pc-input" disabled={carregando}>
                  <option value="">Selecione</option>
                  {clientes.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.nome || c.razaoSocial || c.id}
                    </option>
                  ))}
                </select>
              </div>

              <div className="pc-form-group">
                <label>Selecionar academia</label>
                <select value={academiaId} onChange={(e) => setAcademiaId(e.target.value)} className="pc-input" disabled={!clienteId}>
                  <option value="">Selecione</option>
                  {academias.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.nome || a.codigo || a.id}
                    </option>
                  ))}
                </select>
              </div>

              <label className="pc-form-group pc-form-group-full">
                Observacoes
                <textarea rows={3} className="pc-input" value={observacoes} onChange={(e) => setObservacoes(e.target.value)} />
              </label>

              <div className="pc-form-group pc-form-group-full" style={{ marginTop: 6 }}>
                <strong>Quantidade de Equipamentos por tipo</strong>
              </div>
              <div className="pc-form-group pc-form-group-full">
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 8 }}>
                  {TIPOS_EQUIPAMENTO.map((tipo) => (
                    <div
                      key={tipo}
                      style={{ border: "1px solid rgba(148,163,184,0.2)", borderRadius: 8, padding: "8px 10px", background: "rgba(15,23,42,0.35)" }}
                    >
                      <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13, opacity: 0.8, cursor: "pointer" }}>
                        <div>{tipo}</div>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={quantEquipPorTipo[tipo] || 0}
                          onChange={(e) =>
                            setQuantEquipPorTipo((prev) => ({
                              ...prev,
                              [tipo]: Number(e.target.value) || 0,
                            }))
                          }
                          className="pc-input"
                          style={{ fontWeight: 700 }}
                        />
                        <small style={{ opacity: 0.7 }}>R$ {formatNumero(valoresPreventivaPorTipo[tipo] || 0)}</small>
                      </label>
                    </div>
                  ))}
                </div>
                <div style={{ marginTop: 8, fontWeight: 600 }}>Total: {totalEquipamentos}</div>
                <div style={{ marginTop: 4, fontWeight: 600 }}>Valor equipamentos: R$ {formatNumero(valorEquipamentos)}</div>
              </div>

              <div style={{ gridColumn: "1 / -1", fontWeight: 700, opacity: 0.9, marginTop: 6 }}>
                Endereco e Navegacao
              </div>

              <label className="pc-form-group pc-form-group-full">
                Endereco de destino (academia/cliente)
                <input type="text" value={enderecoDestino} onChange={(e) => setEnderecoDestino(e.target.value)} className="pc-input" />
              </label>

              <div style={{ gridColumn: "1 / -1", display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                <button
                  type="button"
                  className="pc-btn-secundario"
                  onClick={handleCalcularDistancia}
                  disabled={!enderecoDestino || !enderecoOrigem || calculandoDistancia}
                >
                  {calculandoDistancia ? "Calculando..." : "📍 Calcular distância"}
                </button>

                {enderecoDestino && (
                  <button
                    type="button"
                    className="pc-btn-secundario"
                    onClick={() => {
                      const url = gerarUrlGoogleMaps(enderecoDestino, enderecoOrigem || null);
                      window.open(url, "_blank");
                    }}
                  >
                    🗺️ Abrir no Google Maps
                  </button>
                )}

                <label style={{ display: "flex", alignItems: "center", gap: 6, margin: 0 }}>
                  <input
                    type="checkbox"
                    checked={idaVolta}
                    onChange={(e) => setIdaVolta(e.target.checked)}
                    style={{ margin: 0, width: 18, height: 18, cursor: "pointer" }}
                  />
                  <span style={{ fontSize: 13, cursor: "pointer" }}>Ida e Volta</span>
                </label>

                {!enderecoOrigem && (
                  <div style={{ fontSize: 12, color: "#f59e0b", padding: 4 }}>
                    ⚠️ Configure o endereco de origem em Opcoes
                  </div>
                )}
              </div>

              <div style={{ gridColumn: "1 / -1", fontWeight: 700, opacity: 0.9, marginTop: 8 }}>
                Financeiro • Deslocamento
              </div>

              <label className="pc-form-group">
                Km rodados
                <input type="number" min="0" step="0.1" value={kmRodados} onChange={(e) => setKmRodados(e.target.value)} className="pc-input" />
              </label>

              <label className="pc-form-group">
                Valor por km (R$)
                <input type="text" readOnly value={formatNumero(valorKmDeslocamento, 2)} className="pc-input" style={{ opacity: 0.7, cursor: "not-allowed" }} />
                <small style={{ opacity: 0.6, marginTop: 2, display: "block" }}>Configurado em Opcoes / Grupo</small>
              </label>

              <label className="pc-form-group">
                Valor deslocamento (R$)
                <input type="text" value={valorDeslocamento} onChange={(e) => setValorDeslocamento(e.target.value)} className="pc-input" />
              </label>

              <label className="pc-form-group">
                Pedagio (R$)
                <input type="number" min="0" step="0.01" value={valorPedagio} onChange={(e) => setValorPedagio(e.target.value)} className="pc-input" />
              </label>

              <label className="pc-form-group">
                Desconto (R$)
                <input type="number" min="0" step="0.01" value={valorDesconto} onChange={(e) => setValorDesconto(e.target.value)} className="pc-input" />
              </label>

              <label className="pc-form-group">
                Tipo de viagem
                <select value={tipoViagem} onChange={(e) => setTipoViagem(e.target.value)} className="pc-input">
                  <option value="CARRO">Carro</option>
                  <option value="AVIAO">Aviao</option>
                </select>
              </label>

              <label className="pc-form-group">
                Quantidade de ajudantes
                <input type="number" min="0" step="1" value={quantidadeAjudantes} onChange={(e) => setQuantidadeAjudantes(e.target.value)} className="pc-input" />
              </label>

              <label className="pc-form-group">
                Gastos com Almoco (R$)
                <input type="number" min="0" step="0.01" value={gastosAlmoco} onChange={(e) => setGastosAlmoco(e.target.value)} className="pc-input" />
              </label>

              <label className="pc-form-group">
                Previsao de dias
                <input type="number" min="1" step="1" value={previsaoDias} onChange={(e) => setPrevisaoDias(e.target.value)} className="pc-input" />
              </label>

              <label className="pc-form-group pc-form-group-full">
                Valor total (R$)
                <input type="text" readOnly className="pc-input" value={formatNumero(valorTotal)} />
              </label>
            </div>

            <div className="pc-form-actions">
              <button
                type="button"
                className="pc-btn-secundario"
                onClick={() => navigate("/financeiro/preventivas")}
                disabled={salvando}
              >
                Voltar
              </button>
              <button type="submit" className="pc-btn-primario" disabled={salvando || carregando}>
                {salvando ? "Salvando..." : isEdicao ? "Salvar alteracoes" : "Salvar proposta"}
              </button>
            </div>
          </form>
        )}
      </section>
    </LayoutDesktop>
  );
}
